import { projectCanonicalDataset } from '../filterProjection.js';
import { deriveCanonicalEvidenceMetrics } from './evidenceMetrics.js';

/*
 * Process Advisor's existing task filter and period boundaries remain the
 * membership authority during migration. This module does NOT group events by
 * their timestamps: a case belongs to the period containing its legacy t1
 * (the same rule used by the current Evidence history table). Once selected,
 * all of that case's events and canonical extensions contribute to its metrics.
 */
function datePart(value) {
  if (value == null) return '';
  const match = String(value).match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
}

const PERIOD_FIELDS = Object.freeze([
  'work_items', 'variants', 'activities', 'steps', 'cycle_time',
  'std_deviation', 'avg_customer_comments', 'avg_internal_comments',
  'avg_time_spent', 'avg_transitions', 'rework_rate', 'avg_worklogs',
  'avg_cycle_time_started', 'avg_resolution_time', 'avg_touch_time',
  'avg_wait_time', 'avg_admin_time', 'avg_queue_time',
  'avg_flow_efficiency', 'workflow_alignment', 'compliance',
]);

/**
 * @param {object} dataset  Canonical dataset with current runtime enrichment.
 * @param {Array<{period:string,start_date:string,end_date:string,period_type?:string}>} periods
 *   Boundaries are [start_date, end_date), as used by PA's period structure filter.
 * @param {Array<{caseId?:string,caseKey?:string,periodDate:string}>} members
 *   Already selected (filtered===0) legacy tasks, BEFORE any aggregation.
 * @returns {{ rows:Array, diagnostics:Array }}
 */
export function deriveCanonicalPeriodMetrics(dataset, periods = [], members = []) {
  const diagnostics = [];
  const rows = [];
  if (!dataset || !Array.isArray(dataset.cases) || !Array.isArray(dataset.events)) {
    return { rows, diagnostics: [{ reason: 'missing-dataset' }] };
  }

  for (const period of periods) {
    const start = datePart(period?.start_date);
    const end = datePart(period?.end_date);
    if (!start || !end || start >= end) {
      rows.push(null);
      diagnostics.push({ period: period?.period, reason: 'invalid-boundaries' });
      continue;
    }
    const selected = members.filter(m => {
      const d = datePart(m?.periodDate);
      return d && d >= start && d < end;
    });
    const ids = selected.map(m => m?.caseId).filter(v => v !== undefined && v !== null && String(v) !== '');
    const keys = selected.map(m => m?.caseKey).filter(v => v !== undefined && v !== null && String(v) !== '');
    const projected = projectCanonicalDataset(dataset, { caseIds: ids, caseKeys: keys });
    const uniqueMembers = new Set(selected.map(m => String(m?.caseId || m?.caseKey || '')));
    const expected = uniqueMembers.size;
    const actual = projected.cases.length;
    // Never silently substitute incorrect data when native IDs, case keys,
    // or duplicate source records cannot be reconciled.
    if (actual !== expected) {
      rows.push(null);
      diagnostics.push({ period: period.period, reason: 'membership-mismatch', expected, actual });
      continue;
    }
    const metrics = deriveCanonicalEvidenceMetrics(projected);
    const row = {
      period: period.period,
      period_type: period.period_type,
      start_date: start,
      end_date: end,
      periodMetrics: Object.fromEntries(PERIOD_FIELDS.map(key => [key, metrics[key]])),
      metricProvenance: metrics.metricProvenance,
    };
    rows.push(row);
    diagnostics.push({
      period: period.period, reason: 'ok', expected, actual,
      events: projected.events.length, start_date: start, end_date: end,
    });
  }
  return { rows, diagnostics };
}

export const CANONICAL_PERIOD_METRIC_FIELDS = PERIOD_FIELDS;

/**
 * During migration, only replace legacy period fields whose values agree
 * within numeric tolerance. Report differences for investigating edge cases
 * (historical variants, start/end pseudo-activities, lifecycle mappings).
 * Preserve legacy validity fields for existing UI consumers.
 */
export function mergeCanonicalPeriodRow(legacyRow, canonicalRow, tolerance = 0.000001) {
  if (!legacyRow || !canonicalRow || Number(legacyRow.work_items) !== Number(canonicalRow.periodMetrics?.work_items)) {
    return { row: legacyRow, migrated: [], retained: [...PERIOD_FIELDS], differences: [] };
  }
  const row = { ...legacyRow };
  const migrated = [];
  const retained = [];
  const differences = [];
  for (const field of PERIOD_FIELDS) {
    const value = canonicalRow.periodMetrics[field];
    const old = legacyRow[field];
    if (!Number.isFinite(value)) { retained.push(field); continue; }
    // Some older rows do not have a given metric; promote the canonical value.
    if (old !== undefined && old !== null && Number.isFinite(Number(old))) {
      const oldNumber = Number(old);
      const allowed = tolerance * Math.max(1, Math.abs(oldNumber), Math.abs(value));
      if (Math.abs(oldNumber - value) > allowed) {
        retained.push(field);
        differences.push({ field, legacy: oldNumber, canonical: value });
        continue;
      }
    }
    row[field] = value;
    migrated.push(field);
  }
  row.metricProvenance = {
    ...(legacyRow.metricProvenance || {}),
    source: 'canonical-period-parity-gated',
    canonicalFields: migrated,
    legacyFields: retained,
    membership: 'existing-selected-tasks-by-t1',
    startInclusiveEndExclusive: true,
    canonical: canonicalRow.metricProvenance,
  };
  return { row, migrated, retained, differences };
}
